import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { AppHeader } from '@/shared/components/layout/AppHeader'
import { GameweekActionsCard } from '@/features/admin/components/GameweekActionsCard'
import { GameweekScheduleCard } from '@/features/admin/components/GameweekScheduleCard'
import { adminKeys, fetchAdminGameweeks, type AdminGameweek } from '@/features/admin/queries'
import { useCompetitions } from '@/features/league/queries'
import styles from './gameweek.module.css'

export const Route = createFileRoute('/_authenticated/admin/gameweek/$gameweekId')({
  component: AdminGameweekPage,
})

function AdminGameweekPage() {
  const { gameweekId } = Route.useParams()
  const { gw, competitionId, isPending, isError } = useGameweekById(gameweekId)

  return (
    <>
      <AppHeader title="Admin" />
      <div className={styles.page}>
        <Link
          to="/admin"
          search={competitionId ? { competitionId } : {}}
          className={styles.backLink}
        >
          <ChevronLeft size={14} />
          Voltar para rodadas
        </Link>

        {isPending && <div className={styles.empty}>Carregando rodada…</div>}
        {isError && <div className={styles.empty}>Erro ao carregar rodada.</div>}
        {!isPending && !gw && (
          <div className={styles.empty}>
            Rodada não encontrada. Verifique o ID na URL.
          </div>
        )}
        {gw && <GameweekActionsCard gw={gw} />}
        {gw && <GameweekScheduleCard gameweekId={gw.id} gameweekNumber={gw.number} />}
      </div>
    </>
  )
}

// We don't have a direct "get gameweek by id" admin endpoint — the existing
// /v1/gameweeks/{id} returns a different shape. Instead, walk the comp list
// (cheap, all comps fit in one query) and find this GW. Reuses the cached
// admin GW lists from the index page when possible.
function useGameweekById(gameweekId: string): {
  gw:            AdminGameweek | null
  competitionId: string | null
  isPending:     boolean
  isError:       boolean
} {
  const competitions = useCompetitions()

  const allGwsQuery = useQuery({
    queryKey:  [...adminKeys.all, 'gameweeks-all', competitions.data?.map(c => c.id) ?? []],
    queryFn:   async () => {
      const comps = competitions.data ?? []
      const lists = await Promise.all(
        comps.map(async (c) => ({
          competitionId: c.id,
          gws:           await fetchAdminGameweeks(c.id),
        })),
      )
      return lists
    },
    enabled:   !!competitions.data,
    staleTime: 30_000,
  })

  if (competitions.isPending || allGwsQuery.isPending) {
    return { gw: null, competitionId: null, isPending: true, isError: false }
  }
  if (competitions.isError || allGwsQuery.isError) {
    return { gw: null, competitionId: null, isPending: false, isError: true }
  }

  for (const list of allGwsQuery.data ?? []) {
    const found = list.gws.find((g) => g.id === gameweekId)
    if (found) {
      return { gw: found, competitionId: list.competitionId, isPending: false, isError: false }
    }
  }
  return { gw: null, competitionId: null, isPending: false, isError: false }
}
